import { Badge, Box, Card, Flex, Heading } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, useMobileContext } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import surveysService from '../../api/services/surveysService';

const columnHelper = createColumnHelper<Survey>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Surveys() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<Survey>(), []);
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();

	function getSurveyStatusBadgeColor(status: SurveyStatus): string {
		switch (status) {
			case 'enabled':
				return 'green';
			case 'scheduled':
				return 'yellow';
			case 'disabled':
				return 'red';
			default:
				return 'grey';
		}
	}

	function getSurveyStatusTranslation(status: SurveyStatus): string {
		switch (status) {
			case 'enabled':
				return t('general.enabled');
			case 'scheduled':
				return t('general.scheduled');
			case 'disabled':
				return t('general.disabled');
			default:
				return t('general.unknown');
		}
	}

	const columns = [
		columnHelper.accessor('account.name', {
			header: t('general.account'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('surveyStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Badge colorPalette={getSurveyStatusBadgeColor(row.original.surveyStatus)}>{getSurveyStatusTranslation(row.original.surveyStatus)}</Badge>;
			},
		}),
		columnHelper.accessor('surveySubmissionsCount', {
			header: t('general.submissions'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('timeSubmitStart', {
			header: t('general.time_submit_start'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeSubmitStart) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeSubmitStart, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeSubmitEnd', {
			header: t('general.time_submit_end'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeSubmitEnd) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeSubmitEnd, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return dbTimeToUserTime(row.original.timeCreated, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeModified, TIMEZONE);
			},
		}),
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.surveys')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}
			<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
				<Flex display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="surveys"
								keyAttribute="surveyID"
								emptyDataText={t('general.no_surveys_found')}
								fetchFn={(...args) => surveysService.getAllResources(...args)}
								queryParams={{ getRelated: 'account', countRelated: 'surveySubmissions' }}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={[]}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeCreated', desc: true }}
							>
								<Flex gap={2} flexWrap="wrap" ml="auto">
									<DataCollectionTableHeader />
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
